
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";
import { useCreateUsuario } from "@/hooks/useUsuarios";
import { UserRole, getRoleDisplayName } from "@/types/auth";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";
import { SecurityAlert } from "@/components/SecurityAlert";
import { UserPasswordInput } from "@/components/PasswordInput";

export function NovoUsuarioDialog() {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    password: "",
    telefone: "",
    role: "operator" as UserRole,
    setor_departamento: undefined as "vendas" | "financeiro" | "tecnico" | "administrativo" | undefined,
  });

  const createUsuario = useCreateUsuario();
  const { canManageUsers } = useAuth();

  // Verificar se o usuário atual pode criar usuários
  const canCreateUsers = canManageUsers();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      console.log('🔍 DEBUG - Iniciando criação de usuário:', formData);
      
              // Validar se senha foi fornecida
        if (!formData.password || formData.password.trim() === '') {
          toast.error('Senha é obrigatória. Digite uma senha forte.');
          return;
        }

        await createUsuario.mutateAsync({
          ...formData,
          password: formData.password,
          user_role: formData.role
        });
      
      console.log('✅ Usuário criado com sucesso');
      setOpen(false);
      setError(null);
      setFormData({
        nome: "",
        email: "",
        password: "",
        telefone: "",
        role: "operator" as UserRole,
        setor_departamento: undefined,
      });
    } catch (error: any) {
      console.error("❌ Erro ao criar usuário:", error);
      
      // Limpar erro anterior
      setError(null);
      
      // Mensagens de erro mais específicas
      if (error?.message?.includes('não é admin')) {
        setError('Apenas administradores podem criar usuários');
      } else if (error?.message?.includes('não autenticado')) {
        setError('Usuário não autenticado. Faça login novamente.');
      } else if (error?.message?.includes('Edge Function')) {
        setError('Erro no servidor. Tente novamente.');
      } else if (error?.message?.includes('requisitos de segurança')) {
        setError('Senha não atende aos requisitos de segurança');
      } else {
        setError('Erro ao criar usuário: ' + (error?.message || 'Erro desconhecido'));
      }
    }
  };

  if (!canCreateUsers) {
    return null; // Não mostrar o botão se não tiver permissão
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setError(null);
      setFormData({
        nome: "",
        email: "",
        password: "",
        telefone: "",
        role: "operator" as UserRole,
        setor_departamento: undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-primary hover:bg-primary/90">
          <Plus className="mr-2 h-4 w-4" />
          Novo Usuário
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Novo Usuário</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Alert de erro */}
          {error && (
            <SecurityAlert
              type="error"
              title="Erro ao criar usuário"
              message={error}
              showRequirements={error.includes('requisitos de segurança')}
            />
          )}
          
          <div>
            <Label htmlFor="nome">Nome</Label>
            <Input
              id="nome"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          
                    <UserPasswordInput
            value={formData.password}
            onChange={(password) => setFormData({ ...formData, password })}
            showStrengthMeter={false}
          />
          <PasswordStrengthMeter 
            password={formData.password} 
            className="mt-2" 
          />
          
          <div>
            <Label htmlFor="telefone">Telefone</Label>
            <Input
              id="telefone"
              value={formData.telefone}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
            />
          </div>
          
          <div>
            <Label htmlFor="role">Role</Label>
            <Select 
              value={formData.role} 
              onValueChange={(value: UserRole) => 
                setFormData({ ...formData, role: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">{getRoleDisplayName('admin')}</SelectItem>
                <SelectItem value="operator">{getRoleDisplayName('operator')}</SelectItem>
                <SelectItem value="viewer">{getRoleDisplayName('viewer')}</SelectItem>
                <SelectItem value="vendedor">{getRoleDisplayName('vendedor')}</SelectItem>
                <SelectItem value="vendedor_interno">{getRoleDisplayName('vendedor_interno')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="setor">Setor</Label>
            <Select 
              value={formData.setor_departamento || ""} 
              onValueChange={(value: "vendas" | "financeiro" | "tecnico" | "administrativo") => 
                setFormData({ ...formData, setor_departamento: value || undefined })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um setor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vendas">Vendas</SelectItem>
                <SelectItem value="financeiro">Financeiro</SelectItem>
                <SelectItem value="tecnico">Técnico</SelectItem>
                <SelectItem value="administrativo">Administrativo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={createUsuario.isPending}>
              {createUsuario.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
